import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Ruler, Eye, Boxes, Star, BadgeCheck, Compass, Crown } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { formatINR } from "@/context/ConfigContext";
import FavoriteButton from "@/components/FavoriteButton";

const STATUS_BADGE = {
  sold: { text: "SOLD", cls: "bg-slate-900 text-white" },
  under_negotiation: { text: "UNDER NEGOTIATION", cls: "bg-amber-500 text-white" },
  rented: { text: "RENTED", cls: "bg-blue-600 text-white" },
  inactive: { text: "INACTIVE", cls: "bg-slate-500 text-white" },
};

function formatPrice(v) {
  if (v == null) return "-";
  const num = Number(v);
  if (num >= 1_00_00_000) return `₹${(num / 1_00_00_000).toFixed(2)} Cr`;
  if (num >= 1_00_000) return `₹${(num / 1_00_000).toFixed(2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString("en-IN")}`;
}

export default function PropertyCard({ property, index = 0 }) {
  const { t } = useLang();
  // Full URLs (http://, https://) pass through untouched; internal paths route via /files/
  const firstPhoto = property.photo_paths?.[0];
  const cover = firstPhoto
    ? (/^https?:\/\//i.test(firstPhoto) ? firstPhoto : `${API}/files/${firstPhoto}`)
    : "https://images.unsplash.com/photo-1747854805840-9be7d5e360e6?w=800";

  const categoryLabel = t.categories[property.category] || property.category;
  const priceUnit = t.units[property.price_unit] || property.price_unit;

  return (
    <Link
      to={`/property/${property.id}`}
      className="group block rounded-xl overflow-hidden bg-card border border-border shadow-[0_4px_24px_rgb(0,0,0,0.04)] hover:shadow-[0_10px_36px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1"
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={cover}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap max-w-[75%]">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground shadow">
            {categoryLabel}
          </span>
          {property.is_premium && (
            <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-secondary via-yellow-400 to-secondary text-secondary-foreground shadow-md flex items-center gap-1 animate-pulse" data-testid={`premium-badge-${property.id}`}>
              <Crown size={10} /> Premium
            </span>
          )}
          {property.featured && !property.is_premium && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 shadow flex items-center gap-1" data-testid={`featured-badge-${property.id}`}>
              <Star size={10} fill="currentColor" /> Featured
            </span>
          )}
          {property.model_path && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground shadow flex items-center gap-1">
              <Boxes size={10} /> 3D
            </span>
          )}
          {property.owner_kyc_status === "verified" && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-600 text-white shadow flex items-center gap-1" data-testid={`kyc-badge-${property.id}`}>
              <BadgeCheck size={10} /> KYC
            </span>
          )}
          {property.vastu_score != null && (
            <span
              className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shadow flex items-center gap-1 ${
                property.vastu_score >= 9 ? "bg-emerald-700 text-white" :
                property.vastu_score >= 7 ? "bg-green-600 text-white" :
                property.vastu_score >= 5 ? "bg-amber-500 text-slate-900" :
                "bg-red-500 text-white"
              }`}
              title={`Vastu ${property.vastu_score}/10`}
              data-testid={`vastu-badge-${property.id}`}
            >
              <Compass size={10} /> {property.vastu_score}/10
            </span>
          )}
        </div>
        {STATUS_BADGE[property.status] && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest shadow-lg ${STATUS_BADGE[property.status].cls}`} data-testid={`status-badge-${property.id}`}>
              {STATUS_BADGE[property.status].text}
            </span>
          </div>
        )}
        <FavoriteButton propertyId={property.id} className="absolute top-3 right-3 w-9 h-9 shadow" />
        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur text-white text-xs flex items-center gap-1">
          <Eye size={12} /> {property.views || 0}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-lg leading-tight tracking-tight line-clamp-1 group-hover:text-primary transition-colors">
          {property.title}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin size={12} className="text-secondary" />
          <span className="line-clamp-1">{property.city}, {property.state}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-border/60">
          <div>
            <div className="text-lg font-semibold text-primary tracking-tight">
              {formatPrice(property.price_value)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {priceUnit}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold flex items-center gap-1">
              <Ruler size={12} className="text-secondary" />
              {property.size_value}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t.units[property.size_unit] || property.size_unit}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
